/**
 * Énumérations du module Service client, partagées entre le backend (modèle `ServiceClientTicket` +
 * schémas Vine) et le back-office (types UI). Source de vérité unique : toute évolution (ajout d'un
 * statut, d'un canal…) se fait ici.
 */

export const ticketChannels = ['email', 'instagram', 'google', 'whatsapp', 'phone'] as const
export type TicketChannel = (typeof ticketChannels)[number]

export const ticketPriorities = ['P1', 'P2', 'P3', 'P4', 'P5'] as const
export type TicketPriority = (typeof ticketPriorities)[number]

export const ticketStatuses = [
  'new',
  'in_progress',
  'waiting_customer',
  'auto_routed',
  'resolved',
] as const
export type TicketStatus = (typeof ticketStatuses)[number]

export const ticketCloseReasons = ['resolved', 'duplicate'] as const
export type TicketCloseReason = (typeof ticketCloseReasons)[number]

export const ticketChannelLabel: Record<TicketChannel, string> = {
  email: 'E-mail',
  instagram: 'Instagram',
  google: 'Google',
  whatsapp: 'WhatsApp',
  phone: 'Téléphone',
}

export const ticketStatusLabel: Record<TicketStatus, string> = {
  new: 'Nouveau',
  in_progress: 'En cours',
  waiting_customer: 'En attente client',
  auto_routed: 'Routé auto.',
  resolved: 'Résolu',
}

/**
 * Pastille d'alerte affichée sous l'objet d'un ticket (liste + aperçu). `sla` = horloge (délai de
 * 1re réponse), `warning` = triangle (doublon, relance). Calculée côté backend, redescend dans la
 * liste des tickets.
 */
export type TicketAlertKind = 'warning' | 'sla'

export interface TicketAlert {
  kind: TicketAlertKind
  /** Forme liste, ex. « Doublon probable de #4830 ». */
  label: string
  /** Précision affichée seulement dans l'aperçu (doublon : canal de l'autre ticket, ex. « Instagram »). */
  hint?: string
  /** Doublon : id du ticket homologue (cible de la fusion). Évite de reparser l'id depuis `label`. */
  duplicateTicketId?: number
}

/**
 * Référentiel des délais de 1re réponse par priorité, en minutes (P1 < 30 min … P5 < 72 h). Seule
 * source : consommée par le calcul de l'alerte SLA (backend) et par le menu Priorité (back-office).
 */
export const ticketFirstResponseMinutes: Record<TicketPriority, number> = {
  P1: 30,
  P2: 120,
  P3: 480,
  P4: 1440,
  P5: 4320,
}
